import debounce from 'lodash.debounce';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TouchableOpacity, StyleSheet, FlatList } from 'react-native';

import { Box, ShadowBox, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import {
  ListMessagesDocument,
  Maybe,
  MessageReaction,
  useCreateMessageReactionMutation,
  User,
  useRemoveMessageReactionMutation,
} from '@graphql/generated';
import useChatInput from '@hooks/useChatInput';
import useEmoji from '@hooks/useEmoji';
import useMe from '@hooks/useMe';
import theme, { getScaledSize, SCALED_DIMENSIONS } from '@themes/theme';

interface MessageEmojiModalProps {
  bodyHeight: number;
  reaction: Maybe<MessageReaction[]> | undefined;
  onDismiss?: () => void;
}

export const MessageEmojiModal: React.FC<MessageEmojiModalProps> = ({
  bodyHeight,
  reaction,
  onDismiss,
}) => {
  const { me } = useMe();
  const { t } = useTranslation();
  const { selectedMessage, setSelectedMessage } = useChatInput();
  const { setShowMessageEmojiModal, emojiPositionY, setEmojiPositionY } =
    useEmoji();
  const [reactionList, setReactionList] = useState<MessageReaction[]>([]);
  const [reactionDeletedList, setReactionDeletedList] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    reaction && reaction.length > 0 && setReactionList(reaction);
  }, []);

  useEffect(() => {
    if (reaction && reaction.length > 0 && reactionDeletedList.length > 0) {
      let reactionLength = 0;
      for (const item of reaction) {
        reactionLength = reactionLength + (item?.user?.length ?? 0);
      }
      if (reactionLength === reactionDeletedList.length) {
        setShowMessageEmojiModal(false);
      }
    }
  }, [reactionDeletedList]);

  const [removeMessageReactionMutation] = useRemoveMessageReactionMutation({
    refetchQueries: [
      {
        query: ListMessagesDocument,
        variables: { chatId: selectedMessage?.chatId },
      },
    ],
  });

  const [createMessageReactionMutation] = useCreateMessageReactionMutation({
    refetchQueries: [
      {
        query: ListMessagesDocument,
        variables: { chatId: selectedMessage?.chatId },
      },
    ],
  });

  const updateEmoji = (xitem: MessageReaction, uid: string) => {
    let isDeletedReaction = false;
    reactionDeletedList?.forEach((item) => {
      xitem.user?.forEach((user) => {
        if (xitem.id + user.id === item) isDeletedReaction = true;
      });
    });
    if (xitem.user?.some((t: User) => t.id === me?.id) && !isDeletedReaction) {
      if (uid !== me?.id) {
        return;
      }
    }
    setLoading(true);
    if (xitem.user?.some((t: User) => t.id === me?.id) && !isDeletedReaction) {
      removeMessageReactionMutation({
        variables: {
          messageReactionId: xitem.id,
        },
        optimisticResponse: {
          removeMessageReaction: {
            __typename: 'MessageReaction',
            id: xitem.id,
            reaction: xitem.reaction,
            count: Math.max(0, xitem.count - 1),
          },
        },
        onCompleted: () => {
          setReactionDeletedList((prev) => [...prev, xitem.id + me?.id]);
          setLoading(false);
        },
      });
    } else {
      selectedMessage?.id &&
        createMessageReactionMutation({
          variables: {
            messageId: selectedMessage?.id,
            reaction: xitem.reaction,
          },
          optimisticResponse: {
            createMessageReaction: {
              __typename: 'MessageReaction',
              id: xitem.id,
              reaction: xitem.reaction,
              count: reactionDeletedList.includes(xitem.id + me?.id)
                ? xitem.count
                : xitem.count + 1,
              reactionId: xitem.reactionId ?? xitem.id,
            },
          },
          onCompleted: () => {
            setShowMessageEmojiModal(false);
            setEmojiPositionY(0);
            setSelectedMessage(undefined);
            setLoading(false);
          },
        });
    }
  };

  const debouncedUpdateEmoji = useMemo(
    () => debounce(updateEmoji, 500),
    [reactionDeletedList]
  );
  const [isHoveredId, setIsHoveredId] = useState<string | undefined>(undefined);

  const renderItem = (item: MessageReaction) => {
    if (!item?.user) return <Box key={item.id} />;
    const items = [];
    for (const u of item?.user ?? []) {
      if (!reactionDeletedList.includes(item.id + u?.id))
        items.push(
          <TouchableOpacity
            key={item.id + u.id}
            onPress={() => {
              debouncedUpdateEmoji(item, u?.id);
            }}
            onMouseEnter={() => setIsHoveredId(item.id + u.id)}
            onMouseLeave={() => setIsHoveredId(undefined)}
            accessibilityLabel={u.name ?? ''}>
            <Box
              flexDirection='row'
              px='m'
              py='xs'
              alignItems='center'
              backgroundColor={
                isHoveredId === item.id + u.id ? 'grey01' : 'transparent'
              }>
              <Box flex={1}>
                <Text variant='body' color='textSecondary'>
                  {u.id === me?.id ? 'You' : u.name}
                </Text>
                {u.id === me?.id && (
                  <Text
                    variant='metadata'
                    color='grey04'
                    style={{ letterSpacing: 0.2 }}>
                    {t('models:chat.clickToRemoveYourReaction')}
                  </Text>
                )}
              </Box>
              <Text
                style={{
                  fontSize: 24,
                  lineHeight: 32,
                }}>
                {item.reaction}
              </Text>
            </Box>
          </TouchableOpacity>
        );
    }
    return items;
  };

  const getHeight = () => {
    let height = 0;
    for (const item of reactionList) {
      height = height + (item.user?.length ?? 0) * 48;
      if (item?.user?.find((u) => u.id === me?.id)) height = height + 10;
    }
    return 12 * 2 + height - reactionDeletedList.length * 58;
  };

  const getPaddingBottom = () => {
    const emojiModalHeight = Math.min(getHeight(), 12 * 2 + 48 * 5);
    return emojiPositionY < emojiModalHeight + 80
      ? bodyHeight - emojiPositionY - emojiModalHeight + 50
      : bodyHeight - emojiPositionY + 100;
  };

  return (
    <TouchableOpacity
      style={[
        styles.container,
        {
          paddingLeft:
            getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT) +
            theme.spacing.m +
            theme.spacing.s,
          paddingBottom: getPaddingBottom(),
          cursor: 'auto',
        },
      ]}
      onPress={() => {
        onDismiss?.();
        setShowMessageEmojiModal(false);
        setEmojiPositionY(0);
        setSelectedMessage(undefined);
      }}>
      <ShadowBox
        backgroundColor='white'
        py='s'
        borderRadius='xs'
        style={{
          width: 300,
          height: getHeight(),
          maxHeight: 12 * 2 + 48 * 5,
          zIndex: 10000,
        }}>
        {reactionList && (
          <FlatList
            data={reactionList}
            showsVerticalScrollIndicator={false}
            renderItem={({ item }) => {
              return renderItem(item);
            }}
          />
        )}
        {loading && <ActivityIndicatorLoading isChatMessageList={false} />}
      </ShadowBox>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    height: '100%',
    zIndex: 100,
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    borderRadius: theme.borderRadii.xs,
  },
});
